import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';

const DataStoreContext = createContext(null);

export const DataStoreProvider = ({ children }) => {
  const { user } = useAuth();

  // Core data maps
  const [fighters, setFighters] = useState(new Map());
  const [events, setEvents] = useState(new Map());
  const [fights, setFights] = useState(new Map());
  const [votes, setVotes] = useState(new Map());
  const [userVotes, setUserVotes] = useState(new Map());
  const [users, setUsers] = useState(new Map());

  // Loading states
  const [loading, setLoading] = useState({
    fighters: true,
    events: true,
    fights: true,
    votes: true,
    users: true
  });

  // Stream all fighters
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, 'fighters'), orderBy('name', 'asc')),
      (snapshot) => {
        const fightersMap = new Map();
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          fightersMap.set(doc.id, {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
          });
        });
        setFighters(fightersMap);
        setLoading(prev => ({ ...prev, fighters: false }));
      },
      (error) => {
        console.error('Error streaming fighters:', error);
        setLoading(prev => ({ ...prev, fighters: false }));
      }
    );

    return unsubscribe;
  }, []);

  // Stream all events
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, 'events'), orderBy('date', 'desc')),
      (snapshot) => {
        const eventsMap = new Map();
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          eventsMap.set(doc.id, {
            id: doc.id,
            ...data,
            date: data.date?.toDate ? data.date.toDate() : data.date,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
          });
        });
        setEvents(eventsMap);
        setLoading(prev => ({ ...prev, events: false }));
      },
      (error) => {
        console.error('Error streaming events:', error);
        setLoading(prev => ({ ...prev, events: false }));
      }
    );

    return unsubscribe;
  }, []);

  // Stream all fights
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, 'fights'), orderBy('order', 'asc')),
      (snapshot) => {
        const fightsMap = new Map();
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          fightsMap.set(doc.id, {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
          });
        });
        setFights(fightsMap);
        setLoading(prev => ({ ...prev, fights: false }));
      },
      (error) => {
        console.error('Error streaming fights:', error);
        setLoading(prev => ({ ...prev, fights: false }));
      }
    );

    return unsubscribe;
  }, []);

  // Stream all votes (only when authenticated - security rules require auth)
  useEffect(() => {
    // Votes require authentication to read per security rules
    if (!user?.uid) {
      setVotes(new Map());
      setLoading(prev => ({ ...prev, votes: false }));
      return;
    }

    setLoading(prev => ({ ...prev, votes: true }));

    const unsubscribe = onSnapshot(
      collection(db, 'votes'),
      (snapshot) => {
        const votesMap = new Map();
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          votesMap.set(doc.id, {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
          });
        });
        setVotes(votesMap);
        setLoading(prev => ({ ...prev, votes: false }));
      },
      (error) => {
        console.error('Error streaming votes:', error);
        setLoading(prev => ({ ...prev, votes: false }));
      }
    );

    return unsubscribe;
  }, [user?.uid]);

  // Stream user-specific votes when user changes
  useEffect(() => {
    if (!user?.uid) {
      setUserVotes(new Map());
      return;
    }

    const unsubscribe = onSnapshot(
      query(collection(db, 'votes'), where('userId', '==', user.uid)),
      (snapshot) => {
        const userVotesMap = new Map();
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const key = data.round
            ? `${data.fightId}_round_${data.round}`
            : `${data.fightId}_fight`;
          userVotesMap.set(key, data.fighterId);
        });
        setUserVotes(userVotesMap);
      },
      (error) => {
        console.error('Error streaming user votes:', error);
      }
    );

    return unsubscribe;
  }, [user?.uid]);

  // Stream user profiles (for leaderboard and profile data)
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'users'),
      (snapshot) => {
        const usersMap = new Map();
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          usersMap.set(doc.id, {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
          });
        });
        setUsers(usersMap);
        setLoading(prev => ({ ...prev, users: false }));
      },
      (error) => {
        console.error('Error streaming users:', error);
        setLoading(prev => ({ ...prev, users: false }));
      }
    );

    return unsubscribe;
  }, []);

  // Computed getters with local references
  const getFighter = useCallback((fighterId) => {
    return fighters.get(fighterId) || null;
  }, [fighters]);

  const getAllFighters = useCallback(() => {
    return Array.from(fighters.values());
  }, [fighters]);

  const getEvent = useCallback((eventId) => {
    return events.get(eventId) || null;
  }, [events]);

  const getAllEvents = useCallback(() => {
    return Array.from(events.values());
  }, [events]);

  const getFight = useCallback((fightId) => {
    return fights.get(fightId) || null;
  }, [fights]);

  const getAllFights = useCallback(() => {
    return Array.from(fights.values());
  }, [fights]);

  const getFightsForEvent = useCallback((eventId) => {
    return Array.from(fights.values())
      .filter(fight => fight.eventId === eventId)
      .map(fight => ({
        ...fight,
        fighter1: fighters.get(fight.fighter1Id),
        fighter2: fighters.get(fight.fighter2Id)
      }))
      .sort((a, b) => a.order - b.order);
  }, [fights, fighters]);

  const getFightCountForEvent = useCallback((eventId) => {
    return Array.from(fights.values()).filter(fight => fight.eventId === eventId).length;
  }, [fights]);

  const getActiveEvents = useCallback(() => {
    return Array.from(events.values())
      .filter(event => event.isActive)
      .map(event => ({
        ...event,
        fights: getFightsForEvent(event.id)
      }));
  }, [events, getFightsForEvent]);

  const getEnrichedEvent = useCallback((eventId) => {
    const event = events.get(eventId);
    if (!event) return null;

    return {
      ...event,
      fights: getFightsForEvent(eventId)
    };
  }, [events, getFightsForEvent]);

  const getVotesForFight = useCallback((eventId, fightId, round = null) => {
    const fightVotes = Array.from(votes.values()).filter(vote =>
      vote.eventId === eventId &&
      vote.fightId === fightId &&
      vote.type === (round ? 'round_winner' : 'fight_winner') &&
      (round ? vote.round === round : !vote.round)
    );

    // Get the fight to access fighter IDs
    const fight = fights.get(fightId);
    if (!fight) return [];

    // Count votes by fighter
    const voteCount = {};
    fightVotes.forEach(vote => {
      voteCount[vote.fighterId] = (voteCount[vote.fighterId] || 0) + 1;
    });

    const totalVotes = fightVotes.length;

    // Include all fighters in the fight, even those with 0 votes
    const allFighterIds = [fight.fighter1Id, fight.fighter2Id];

    return allFighterIds.map(fighterId => {
      const fighter = fighters.get(fighterId);
      const count = voteCount[fighterId] || 0;
      return {
        fighterId,
        fighterName: fighter?.name || 'Unknown Fighter',
        votes: count,
        percentage: totalVotes > 0 ? (count / totalVotes) * 100 : 0
      };
    });
  }, [votes, fighters, fights]);

  const getUserVote = useCallback((fightId, round = null) => {
    const key = round ? `${fightId}_round_${round}` : `${fightId}_fight`;
    return userVotes.get(key) || null;
  }, [userVotes]);

  const hasUserVoted = useCallback((fightId, round = null) => {
    const key = round ? `${fightId}_round_${round}` : `${fightId}_fight`;
    return userVotes.has(key);
  }, [userVotes]);

  const getFilteredFighters = useCallback((filters = {}) => {
    let filtered = Array.from(fighters.values());

    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(fighter =>
        fighter.name.toLowerCase().includes(term) ||
        fighter.weightClass?.toLowerCase().includes(term)
      );
    }

    if (filters.weightClass) {
      filtered = filtered.filter(fighter => fighter.weightClass === filters.weightClass);
    }

    if (filters.status) {
      filtered = filtered.filter(fighter => fighter.status === filters.status);
    }

    return filtered;
  }, [fighters]);

  // User-related getters
  const getUser = useCallback((userId) => {
    return users.get(userId) || null;
  }, [users]);

  const getAllUsers = useCallback(() => {
    return Array.from(users.values());
  }, [users]);

  // User statistics calculations using in-memory data
  const getUserVotingStats = useCallback((userId) => {
    // Get all votes by this user from in-memory data
    const userVotesList = Array.from(votes.values()).filter(vote => vote.userId === userId);

    // Calculate basic statistics
    const totalVotes = userVotesList.length;
    const fightWinnerVotes = userVotesList.filter(vote => vote.type === 'fight_winner');
    const roundWinnerVotes = userVotesList.filter(vote => vote.type === 'round_winner');

    // Get unique events and fights
    const uniqueEvents = new Set(userVotesList.map(vote => vote.eventId));
    const uniqueFights = new Set(userVotesList.map(vote => vote.fightId));

    // Calculate accuracy using in-memory fight data
    let correctPredictions = 0;
    let totalCompletedFights = 0;

    fightWinnerVotes.forEach(vote => {
      const fight = fights.get(vote.fightId);
      if (fight?.winnerId) {
        totalCompletedFights++;
        if (fight.winnerId === vote.fighterId) {
          correctPredictions++;
        }
      }
    });

    const accuracy = {
      correctPredictions,
      totalCompletedFights,
      percentage: totalCompletedFights > 0 ? (correctPredictions / totalCompletedFights) * 100 : 0
    };

    // Get voting activity by month
    const activity = {};
    userVotesList.forEach(vote => {
      const date = vote.createdAt;
      const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;

      if (!activity[monthKey]) {
        activity[monthKey] = { total: 0, fightWinner: 0, roundWinner: 0 };
      }

      activity[monthKey].total++;
      if (vote.type === 'fight_winner') {
        activity[monthKey].fightWinner++;
      } else if (vote.type === 'round_winner') {
        activity[monthKey].roundWinner++;
      }
    });

    const votingActivity = Object.entries(activity)
      .map(([month, data]) => ({ month, ...data }))
      .sort((a, b) => a.month.localeCompare(b.month));

    // Get favorite weight classes using in-memory data
    const weightClassCount = {};
    fightWinnerVotes.forEach(vote => {
      const fight = fights.get(vote.fightId);
      if (fight?.weightClass) {
        weightClassCount[fight.weightClass] = (weightClassCount[fight.weightClass] || 0) + 1;
      }
    });

    const favoriteWeightClasses = Object.entries(weightClassCount)
      .map(([weightClass, count]) => ({ weightClass, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Sort votes by date for join/last vote dates
    const sortedVotes = [...userVotesList].sort((a, b) => a.createdAt - b.createdAt);

    return {
      totalVotes,
      fightWinnerVotes: fightWinnerVotes.length,
      roundWinnerVotes: roundWinnerVotes.length,
      eventsParticipated: uniqueEvents.size,
      fightsVotedOn: uniqueFights.size,
      accuracy,
      votingActivity,
      favoriteWeightClasses,
      joinDate: sortedVotes.length > 0 ? sortedVotes[0].createdAt : null,
      lastVote: sortedVotes.length > 0 ? sortedVotes[sortedVotes.length - 1].createdAt : null
    };
  }, [votes, fights]);

  const getUserRecentVotes = useCallback((userId, limitCount = 10) => {
    // Get user votes from in-memory data
    const userVotesList = Array.from(votes.values())
      .filter(vote => vote.userId === userId)
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, limitCount);

    // Enrich with fighter and event data from in-memory maps
    return userVotesList.map(vote => {
      const event = events.get(vote.eventId);
      const fight = fights.get(vote.fightId);
      const fighter1 = fight ? fighters.get(fight.fighter1Id) : null;
      const fighter2 = fight ? fighters.get(fight.fighter2Id) : null;

      // Determine which fighter the user voted for
      let votedFighterName = 'Unknown Fighter';
      if (vote.fighterId === fight?.fighter1Id) {
        votedFighterName = fighter1?.name || 'Unknown Fighter';
      } else if (vote.fighterId === fight?.fighter2Id) {
        votedFighterName = fighter2?.name || 'Unknown Fighter';
      }

      return {
        ...vote,
        eventName: event?.name || 'Unknown Event',
        fightData: fight,
        fighter1Name: fighter1?.name || 'Unknown Fighter',
        fighter2Name: fighter2?.name || 'Unknown Fighter',
        votedFighterName
      };
    });
  }, [votes, events, fights, fighters]);

  const getLeaderboard = useCallback((limitCount = 50) => {
    const allUsers = Array.from(users.values());
    const leaderboardData = [];

    allUsers.forEach(userData => {
      // Skip admin users if needed (uncomment if desired)
      // if (userData.isAdmin) return;

      const stats = getUserVotingStats(userData.id);

      leaderboardData.push({
        userId: userData.id,
        displayName: userData.displayName || 'Anonymous User',
        email: userData.email,
        totalVotes: stats.totalVotes,
        accuracy: stats.accuracy.percentage,
        correctPredictions: stats.accuracy.correctPredictions,
        eventsParticipated: stats.eventsParticipated,
        joinDate: userData.createdAt
      });
    });

    // Sort by correct predictions (most correct votes), then by accuracy as tiebreaker
    return leaderboardData
      .sort((a, b) => {
        if (a.correctPredictions !== b.correctPredictions) {
          return b.correctPredictions - a.correctPredictions;
        }
        if (a.accuracy !== b.accuracy) {
          return b.accuracy - a.accuracy;
        }
        if (a.totalVotes !== b.totalVotes) {
          return b.totalVotes - a.totalVotes;
        }
        // Final tiebreaker: alphabetical by display name
        return a.displayName.localeCompare(b.displayName);
      })
      .slice(0, limitCount);
  }, [users, getUserVotingStats]);

  const getUserRank = useCallback((userId) => {
    const leaderboard = getLeaderboard(1000); // Get more for accurate ranking
    const userIndex = leaderboard.findIndex(user => user.userId === userId);

    return {
      rank: userIndex >= 0 ? userIndex + 1 : null,
      totalUsers: leaderboard.length,
      percentile: userIndex >= 0 ? ((leaderboard.length - userIndex) / leaderboard.length) * 100 : null
    };
  }, [getLeaderboard]);

  // Fighter statistics calculations using in-memory data
  const getFighterStats = useCallback((fighterId) => {
    // Get all fights for this fighter from in-memory data
    const allFights = Array.from(fights.values())
      .filter(fight => fight.fighter1Id === fighterId || fight.fighter2Id === fighterId)
      .map(fight => ({
        ...fight,
        position: fight.fighter1Id === fighterId ? 'fighter1' : 'fighter2'
      }));

    // Calculate statistics
    const totalFights = allFights.length;
    const completedFights = allFights.filter(fight => fight.status === 'completed');
    const wins = completedFights.filter(fight => fight.winnerId === fighterId).length;
    const losses = completedFights.filter(fight => fight.winnerId && fight.winnerId !== fighterId).length;
    const draws = completedFights.filter(fight => fight.status === 'completed' && !fight.winnerId).length;

    // Get recent fights (last 5) with full data
    const recentFightsData = allFights
      .sort((a, b) => {
        const dateA = a.updatedAt || a.createdAt || new Date(0);
        const dateB = b.updatedAt || b.createdAt || new Date(0);
        return dateB - dateA;
      })
      .slice(0, 5)
      .map((fight) => {
        // Get opponent fighter data from in-memory maps
        const opponentId = fight.position === 'fighter1' ? fight.fighter2Id : fight.fighter1Id;
        const opponent = fighters.get(opponentId);

        // Get event data from in-memory maps
        const event = events.get(fight.eventId);

        return {
          ...fight,
          opponent,
          eventName: event?.name || 'Unknown Event',
          eventDate: event?.date
        };
      });

    // Calculate win percentage
    const winPercentage = completedFights.length > 0 ? (wins / completedFights.length) * 100 : 0;

    // Get weight class stats
    const weightClassFights = {};
    allFights.forEach(fight => {
      if (fight.weightClass) {
        weightClassFights[fight.weightClass] = (weightClassFights[fight.weightClass] || 0) + 1;
      }
    });

    return {
      totalFights,
      wins,
      losses,
      draws,
      winPercentage,
      recentFights: recentFightsData,
      weightClassBreakdown: Object.entries(weightClassFights).map(([weightClass, count]) => ({
        weightClass,
        fights: count
      }))
    };
  }, [fights, fighters, events]);

  // Event analytics calculations using in-memory data
  const getEventAnalytics = useCallback((eventId) => {
    // Get event votes from in-memory data
    const eventVotes = Array.from(votes.values()).filter(vote => vote.eventId === eventId);
    const eventFights = Array.from(fights.values()).filter(fight => fight.eventId === eventId);

    // Vote statistics
    const totalVotes = eventVotes.length;
    const fightWinnerVotes = eventVotes.filter(vote => vote.type === 'fight_winner');
    const roundWinnerVotes = eventVotes.filter(vote => vote.type === 'round_winner');

    // User engagement
    const uniqueUsers = new Set(eventVotes.map(vote => vote.userId));
    const totalUsers = uniqueUsers.size;
    const averageVotesPerUser = totalUsers > 0 ? totalVotes / totalUsers : 0;

    // Fight analytics
    const fightAnalytics = eventFights.map(fight => {
      const fightVotes = eventVotes.filter(vote => vote.fightId === fight.id);

      // Vote distribution for this fight
      const voteDistribution = {};
      fightVotes.forEach(vote => {
        voteDistribution[vote.fighterId] = (voteDistribution[vote.fighterId] || 0) + 1;
      });

      // Prediction accuracy (if fight is completed)
      let predictionAccuracy = null;
      if (fight.status === 'completed' && fight.winnerId) {
        const correctVotes = fightVotes.filter(vote =>
          vote.type === 'fight_winner' && vote.fighterId === fight.winnerId
        ).length;
        const totalFightVotes = fightVotes.filter(vote => vote.type === 'fight_winner').length;
        predictionAccuracy = totalFightVotes > 0 ? (correctVotes / totalFightVotes) * 100 : 0;
      }

      // Get fighter names
      const fighter1 = fighters.get(fight.fighter1Id);
      const fighter2 = fighters.get(fight.fighter2Id);

      return {
        fightId: fight.id,
        fightName: `${fighter1?.name || 'Fighter 1'} vs ${fighter2?.name || 'Fighter 2'}`,
        status: fight.status,
        winnerId: fight.winnerId,
        totalVotes: fightVotes.length,
        voteDistribution,
        predictionAccuracy,
        engagement: fightVotes.length
      };
    });

    // Overall statistics
    const completedFights = fightAnalytics.filter(f => f.status === 'completed');
    const averagePredictionAccuracy = completedFights.length > 0
      ? completedFights.reduce((sum, f) => sum + (f.predictionAccuracy || 0), 0) / completedFights.length
      : 0;

    // Vote timeline (votes by hour)
    const voteTimeline = {};
    eventVotes.forEach(vote => {
      const hour = vote.createdAt.getHours();
      voteTimeline[hour] = (voteTimeline[hour] || 0) + 1;
    });

    return {
      voteStats: {
        totalVotes,
        fightWinnerVotes: fightWinnerVotes.length,
        roundWinnerVotes: roundWinnerVotes.length,
        averagePredictionAccuracy
      },
      userEngagement: {
        totalUsers,
        averageVotesPerUser,
        engagementRate: totalUsers // Could be normalized by event capacity
      },
      fightStats: fightAnalytics,
      timelineData: Object.entries(voteTimeline).map(([hour, count]) => ({
        hour: parseInt(hour),
        votes: count
      })).sort((a, b) => a.hour - b.hour),
      generatedAt: new Date()
    };
  }, [votes, fights, fighters]);

  const isLoading = Object.values(loading).some(Boolean);

  const value = {
    // Data
    fighters,
    events,
    fights,
    votes,
    userVotes,
    users,

    // Loading states
    loading,
    isLoading,

    // Getters
    getFighter,
    getAllFighters,
    getEvent,
    getAllEvents,
    getActiveEvents,
    getFight,
    getAllFights,
    getFightsForEvent,
    getFightCountForEvent,
    getEnrichedEvent,
    getVotesForFight,
    getUserVote,
    hasUserVoted,
    getFilteredFighters,
    getUser,
    getAllUsers,

    // User statistics (consolidated from userService)
    getUserVotingStats,
    getUserRecentVotes,
    getLeaderboard,
    getUserRank,

    // Fighter statistics (consolidated from fighterService)
    getFighterStats,

    // Event analytics (consolidated from analyticsService)
    getEventAnalytics,

    // Computed data
    fighterCount: fighters.size,
    eventCount: events.size,
    fightCount: fights.size,
    voteCount: votes.size,
    userCount: users.size
  };

  return (
    <DataStoreContext.Provider value={value}>
      {children}
    </DataStoreContext.Provider>
  );
};

export const useDataStore = () => {
  const context = useContext(DataStoreContext);
  if (!context) {
    throw new Error('useDataStore must be used within a DataStoreProvider');
  }
  return context;
};

export default DataStoreContext;
